import type { PublicBatch } from "@homesite/api-contract";
import { queryKeys } from "@homesite/queries";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/ApiClientContext";
import type { PaginationInfo } from "./useLoanTimeline";

export interface BatchesOptions {
	page?: number;
	limit?: number;
	chainId?: number;
	anchored?: boolean;
}

export interface BatchesData {
	batches: PublicBatch[];
	pagination: PaginationInfo | null;
	loading: boolean;
	error: Error | null;
}

/**
 * Fetches a paginated list of merkle batches with optional chain and anchor filters.
 */
export function useBatches(opts: BatchesOptions = {}): BatchesData {
	const client = useApiClient();
	const page = opts.page ?? 1;
	const limit = opts.limit ?? 20;

	const query = useQuery({
		queryKey: queryKeys.publicVerification.batches.list(page, limit, {
			chainId: opts.chainId,
			anchored: opts.anchored,
		}),
		queryFn: async () => {
			const result = await client.publicVerification.getBatches({
				query: {
					page,
					limit,
					chainId: opts.chainId,
					anchored:
						opts.anchored === undefined
							? undefined
							: opts.anchored
								? "true"
								: "false",
				},
			});
			if (result.status === 200) {
				return result.body;
			}
			throw new Error("Failed to fetch batches");
		},
	});

	return {
		batches: query.data?.items ?? [],
		pagination: query.data
			? {
					page: query.data.page,
					limit: query.data.limit,
					total: query.data.total,
					totalPages: query.data.totalPages,
				}
			: null,
		loading: query.isLoading,
		error: query.error,
	};
}
